import { documentsTabUrl } from './dossiers/virtualEmailFolder';
import { taskDeepLinkPath } from './tasks/taskDeepLink';

export const UNIVERSAL_SEARCH_MIN_CHARS = 3;
export const UNIVERSAL_SEARCH_LIMIT_PER_KIND = 5;
/** Page de résultats : nombre de résultats chargés par famille. */
export const UNIVERSAL_SEARCH_PAGE_LIMIT = 50;
export const UNIVERSAL_SEARCH_PAGE_PATH = '/recherche';

export type UniversalSearchKind =
  | 'dossier'
  | 'contact'
  | 'ticket'
  | 'file'
  | 'email'
  | 'calendar'
  | 'task'
  | 'collaborator';

export type CalendarSearchKind = 'hearing' | 'prescription';

export interface UniversalSearchHit {
  kind: UniversalSearchKind;
  id: string;
  title: string;
  subtitle?: string;
  dossierId?: string | null;
  dossierType?: string | null;
  dossierStage?: string | null;
  archivedAt?: string | null;
  closedAt?: string | null;
  contactCategoryLabel?: string | null;
  calendarDate?: string | null;
  calendarKind?: CalendarSearchKind | null;
}

export interface UniversalSearchSection {
  kind: UniversalSearchKind;
  label: string;
  hits: UniversalSearchHit[];
}

export type UniversalSearchAction = { type: 'navigate'; to: string };

const KIND_ORDER: UniversalSearchKind[] = [
  'dossier',
  'contact',
  'ticket',
  'file',
  'email',
  'calendar',
  'task',
  'collaborator',
];

const KIND_LABELS: Record<UniversalSearchKind, string> = {
  dossier: 'Dossiers',
  contact: 'Contacts',
  ticket: 'Demandes',
  file: 'Documents',
  email: 'Emails',
  calendar: 'Calendrier',
  task: 'Tâches',
  collaborator: 'Équipe',
};

/** Adresse de la page de résultats pour une recherche. */
export function universalSearchPagePath(query: string): string {
  const term = query.trim();
  return term ? `${UNIVERSAL_SEARCH_PAGE_PATH}?q=${encodeURIComponent(term)}` : UNIVERSAL_SEARCH_PAGE_PATH;
}

export function isUniversalSearchReady(query: string): boolean {
  return query.trim().length >= UNIVERSAL_SEARCH_MIN_CHARS;
}

export function groupUniversalSearchHits(
  hits: UniversalSearchHit[],
  limitPerKind = UNIVERSAL_SEARCH_LIMIT_PER_KIND,
): UniversalSearchSection[] {
  const byKind = new Map<UniversalSearchKind, UniversalSearchHit[]>();
  for (const hit of hits) {
    const list = byKind.get(hit.kind) ?? [];
    if (list.length < limitPerKind) list.push(hit);
    byKind.set(hit.kind, list);
  }

  return KIND_ORDER.flatMap((kind) => {
    const kindHits = byKind.get(kind);
    if (!kindHits?.length) return [];
    return [{ kind, label: KIND_LABELS[kind], hits: kindHits }];
  });
}

export function flattenUniversalSearchHits(
  sections: UniversalSearchSection[],
): UniversalSearchHit[] {
  return sections.flatMap((section) => section.hits);
}

export function calendarSearchHref(
  dossierId: string | null | undefined,
  date: string | null | undefined,
): string {
  if (!dossierId) return '/calendrier';
  const sp = new URLSearchParams();
  if (date) sp.set('date', date);
  sp.set('dossier', dossierId);
  return `/calendrier?${sp.toString()}`;
}

export function resolveUniversalSearchAction(
  hit: UniversalSearchHit,
): UniversalSearchAction {
  switch (hit.kind) {
    case 'dossier':
      return { type: 'navigate', to: `/dossiers/${hit.id}` };
    case 'contact':
      return { type: 'navigate', to: `/contacts/${hit.id}` };
    case 'ticket':
      return { type: 'navigate', to: `/tickets/${hit.id}` };
    case 'file':
      if (!hit.dossierId) return { type: 'navigate', to: '/dossiers' };
      return { type: 'navigate', to: `/dossiers/${hit.dossierId}/documents` };
    case 'email':
      if (!hit.dossierId) return { type: 'navigate', to: '/dossiers' };
      return { type: 'navigate', to: documentsTabUrl(hit.dossierId, true) };
    case 'calendar':
      return { type: 'navigate', to: calendarSearchHref(hit.dossierId, hit.calendarDate) };
    case 'task':
      return { type: 'navigate', to: taskDeepLinkPath(hit.id) };
    case 'collaborator':
      return { type: 'navigate', to: `/equipe/${hit.id}` };
  }
}

export function universalSearchHref(hit: UniversalSearchHit): string {
  return resolveUniversalSearchAction(hit).to;
}

export function shouldSpaActivateSearchHit(event: {
  button: number;
  metaKey: boolean;
  ctrlKey: boolean;
  shiftKey: boolean;
  altKey: boolean;
}): boolean {
  return (
    event.button === 0 &&
    !event.metaKey &&
    !event.ctrlKey &&
    !event.shiftKey &&
    !event.altKey
  );
}

export function shouldOpenSearchHitInNewTab(event: {
  key: string;
  ctrlKey: boolean;
  metaKey: boolean;
}): boolean {
  return event.key === 'Enter' && (event.ctrlKey || event.metaKey);
}
